import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MaybeAsync } from '@angular/router';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { ButtonTheme } from '@shared/button/button.component';
import { ScoreBenchmark } from '@shared/enums/score-benchmark';
import { ZbpIconComponent } from '@shared/zbp-icon/zbp-icon';
import { BehaviorSubject } from 'rxjs';
import { ZbpIconNames } from '../../login/branded-login/login-form/login-form-model';

/** A collection of data types for {@link ZbpDataTableComponent}. */
export namespace ZbpDataTable {

  export enum TableTheme {
    blueAndWhite = 'blueAndWhite',
    transparent = 'transparent',

    /**
     * Uses blueAndWhite and adds override for report printing student activity reports.
     */
    blueAndWhiteStudentActivityReports = 'blueAndWhite styled-table--student-activity-reports',
  }

  interface CommonCellProperties {
    columnSpan?: number,
    borderBottom?: string,
  }

  /** See {@link TextCell}. */
  export const TEXT_CELL = 'text';

  /** Displays as just raw text in the table. */
  export interface TextCell extends CommonCellProperties {
    type: typeof TEXT_CELL;
    text: string,
    tooltip?: string,
    ariaLabel?: string,
    textAlign?: string,
    backgroundColor?: string,
    borderRight?: string,
    contentAlign?: string,
    markRowHeader?: boolean,
    fontWeight?: number,
  }

  /** See {@link PillCell}. */
  export const PILL_CELL = 'pill';

  /** Displays as text in a pill in the table. */
  export interface PillCell extends CommonCellProperties {
    type: typeof PILL_CELL;
    text: string,
    tooltip?: string,
    ariaLabel?: string,
    textColor?: string,
    backgroundColor?: string,
    cellBackgroundColor?: string,
    pillWidth?: string,
    lineHeight?: string,
    borderColor?: string,
    iconName?: ZbpIconNames,
    justifyItems?: string,
    onClick?: () => void,
  }

  /** See {@link HtmlCell}. */
  export const HTML_CELL = 'html';

  /** Displays as html in the table. */
  export interface HtmlCell extends CommonCellProperties {
    type: typeof HTML_CELL;
    html: string,
    ariaLabel?: string,
    lineHeight?: string,
    tooltip?: string,
  }

  /** See {@link TextListCell}. */
  export const TEXT_LIST_CELL = 'text-list';

  /** Displays as a comma-separated list in the table, using `ul` and `li` to preserve semantics. */
  export interface TextListCell extends CommonCellProperties {
    type: typeof TEXT_LIST_CELL;
    textList: string[];
    tooltip?: string,
    ariaLabel?: string,
  }

  /** See {@link SimpleListCell}. */
  export const SIMPLE_LIST_CELL = 'simple-list';

  export interface SimpleListItem {
    text: string,
    tooltip?: string,
    ariaLabel?: string,
    onClick?: () => void,
  }

  /** Displays as a comma-separated list in the table, using `ul` and `li` to preserve semantics. */
  export interface SimpleListCell extends CommonCellProperties {
    type: typeof SIMPLE_LIST_CELL;
    itemList: SimpleListItem[];
    textAlign?: string,
    theme?: ButtonTheme,
    contentAlign?: string,
  }

  /** See {@link LinkCell}. */
  export const LINK_CELL = 'link';

  /** Displays as a text-link styled button in the table, and allows the caller to provide an `onClick` callback. */
  export interface LinkCell extends CommonCellProperties {
    type: typeof LINK_CELL;
    text: string;
    onClick: () => void,
    tooltip?: string,
    ariaLabel?: string,
    role?: 'button' | 'link',
    borderRight?: string,
    textAlign?: string,
    theme?: ButtonTheme,
  }

  /** Configuration for {@link ActionListCell}.*/
  export interface Action {
    iconName: ZbpIconNames;
    disabled?: boolean,
    onClick?: () => void,
    tooltip?: string,
    ariaLabel?: string,
    internalId?: string,
  }

  /** See {@link ActionListCell}. */
  export const ACTION_LIST_CELL = 'action-list';

  /** Displays as a group of icon buttons in the table, and allows each action to be specified individually. */
  export interface ActionListCell extends CommonCellProperties {
    type: typeof ACTION_LIST_CELL,
    actions: Action[],
  }

  /** See {@link NumericCell}. */
  export const NUMERIC_CELL = 'numeric-cell';

  /**
   * Cell for numeric input
   */
  export interface NumericCell extends CommonCellProperties {
    showMax: boolean;
    constrainToLimits: boolean;
    placeholder?: string;
    type: typeof NUMERIC_CELL,
    formControl: FormControl,
    maxValue: number,
    allowMinusToNull?: boolean,
    ariaLabel?: string,
    subtractButtonArialabel?: string,
    addButtonArialabel?: string,
    disabledTooltip?: string,
    allowNullValue?: boolean,
    applyConstraintOnNullInput?: boolean,
  }

  /** See {@link CheckboxCell}. */
  export const CHECKBOX_CELL = 'checkbox-cell';

  /**
   * Displays as an input checkbox as a Form Control for a form in the table
   * Allows the caller to provide an additional click handler that will be called after the checkbox is toggled.
   * Can take in custom ariaLabel for better accessibility.
   * If ariaLabel not provided, defaults to ariaLabel of 'checkbox' + (index + 1).
   */
  export interface CheckboxCell extends CommonCellProperties {
    type: typeof CHECKBOX_CELL,
    formControlName: string,
    ariaLabel?: string,
    disabledTooltip?: string,
    /** Additional click handler that can be provided */
    additionalClickHandler?: () => void,
    /** Additional change handler that can be provided */
    additionalChangeHandler?: () => void,
  }

  /** See {@link RadioCell}. */
  export const RADIO_CELL = 'radio-cell';

  /**
   * Displays as an input radio as a Form Control for a form in the table
   * Can take in custom ariaLabel for better accessibility.
   * If ariaLabel not provided, defaults to ariaLabel of 'radio' + (index + 1).
   */
  export interface RadioCell extends CommonCellProperties {
    type: typeof RADIO_CELL,
    formControlName: string,
    radioValue: string,
    ariaLabel?: string,
    tabIndex?: number,
  }

  /** See {@link BehaviorSubjectCell}. */
  export const BEHAVIOR_SUBJECT_CELL = 'behavior-subject-cell';

  /** Displays the value of Behavior Subject in the table */
  export interface BehaviorSubjectCell extends CommonCellProperties {
    type: typeof BEHAVIOR_SUBJECT_CELL,
    value: BehaviorSubject<any>,
    tooltip?: string,
    ariaLabel?: string,
  }

  /** See {@link ScoreBenchmarkCell}. */
  export const SCORE_BENCHMARK_CELL = 'score-benchmark-cell';

  /** Displays the value of Behavior Subject in the table */
  export interface ScoreBenchmarkCell extends CommonCellProperties {
    type: typeof SCORE_BENCHMARK_CELL,
    scoreBenchmark?: ScoreBenchmark,
    tooltip?: string,
    ariaLabel?: string,
  }

  /** See {@link IconCell}. */
  export const ICON_CELL = 'icon-cell';

  /** Displays the value of zbp icon in the table */
  export interface IconCell extends CommonCellProperties {
    type: typeof ICON_CELL,
    iconName: ZbpIconNames,
    iconSize?: ZbpIconComponent['iconSize'],
    primaryColor?: string,
    secondaryColor?: string,
    tooltip?: string,
    ariaLabel?: string,
    arialLabelTranslationKey?: string,
  }

  export const COLLAPSIBLE_TABLE_TYPE = 'collapsible-table-type';
  export type CollapsibleTableType = {
    type: typeof COLLAPSIBLE_TABLE_TYPE,
    columns,
    rows,
  }

  export const COLLAPSIBLE_IMAGE_TYPE = 'collapsible-image-type';
  export type CollapsibleImageType = {
    type: typeof COLLAPSIBLE_IMAGE_TYPE,
    src,
  }

  export const COLLAPSIBLE_FLUENCY_AUDIO_TYPE = 'collapsible-fluency-audio-type';
  export type CollapsibleFluencyAudioType = {
    type: typeof COLLAPSIBLE_FLUENCY_AUDIO_TYPE,
    instructionsUrl: string,
    audioSrc: MaybeAsync<string>,
  }

  export type CollapsibleContentType = (
    CollapsibleTableType
    | CollapsibleImageType
    | CollapsibleFluencyAudioType
  );

  /** See {@link CollapsibleCell}. */
  export const COLLAPSIBLE_CELL = 'collapsible-cell';

  /** Displays the value of Behavior Subject in the table */
  export interface CollapsibleCell extends CommonCellProperties {
    type: typeof COLLAPSIBLE_CELL,
    content: CollapsibleContentType[],
    tooltip?: string,
    ariaLabel?: string,
  }

  /**
   * Represents a cell in a row for {@link ZbpDataTableComponent}.
   *
   * __Developer Notes:__
   * - To add more cell types, continue to expand this list, and implement the visuals in the component template.
   * - The `type` field is used to make the type safety work.
   *    - Example: `cell.type === 'action-list'` will make typescript collapse the type to `ActionListCell`,
   *        giving better intellisense in the IDE and better compile-time checking.
   */
  export type Cell = (
    TextCell
    | TextListCell
    | SimpleListCell
    | LinkCell
    | ActionListCell
    | CheckboxCell
    | BehaviorSubjectCell
    | RadioCell
    | HtmlCell
    | ScoreBenchmarkCell
    | CollapsibleCell
    | IconCell
    | NumericCell
    | PillCell
    );

  /** Represents a row for {@link ZbpDataTableComponent}. */
  export interface Row {
    /** If provided, will be used with Angular trackBy setting when looping over the rows.
     * Otherwise, the index of the data will be used. */
    trackById?: string,
    /** A mapping of a column key to a cell in the row.
     * __The key MUST match a column key in order to appear in the table.__ */
    cells: Record<string, Cell>,
    /** Boolean for if the row should be disabled */
    isDisabled?: boolean,
    /** Tooltip to be displayed when row is hovered over */
    tooltip?: string,
  }

  export interface CommonColumnProperties {
    /** Used for tracking the column.
     * __The key MUST match the backend field for the sorting to work.__ */
    key: string,

    /** A tooltip to display when the user hovers over the header. */
    tooltip?: string,

    /** column span this column **/
    columnSpan?: number,

    /** how the text should be aligned */
    textAlign?: string,

    /** column width */
    columnWidth?: string,

    /** screen reader accessibility label */
    ariaLabel?: string,

    /** A translation key that overrides {@link ariaLabel} */
    ariaLabelTranslationKey?: string;
  }

  /** See {@link SimpleColumn}. */
  export const SIMPLE_COLUMN = 'simple-column';

  /** Represents a simple column for {@link ZbpDataTableComponent}. */
  export interface SimpleColumn extends CommonColumnProperties {
    type: typeof SIMPLE_COLUMN;

    /** The display string to show in the table header. */
    header?: string;

    /** A translation key that overrides {@link header} */
    headerTranslationKey?: string;

    /** screen reader accessibility label */
    ariaLabel?: string,

    /** A translation key that overrides {@link ariaLabel} */
    ariaLabelTranslationKey?: string;
  }

  /** See {@link BlankColumn}. */
  export const BLANK_COLUMN = 'blank-column';

  /**
   * Represents a blank column for {@link ZbpDataTableComponent}
   * Requires the ariaLabel to be given for screen-reader support
   */
  export interface BlankColumn extends CommonColumnProperties {
    type: typeof BLANK_COLUMN;
  }

  /** See {@link SortableColumn}. */
  export const SORTABLE_COLUMN = 'sortable-column';

  /** Represents a sortable column for {@link ZbpDataTableComponent}. */
  export interface SortableColumn extends CommonColumnProperties {
    type: typeof SORTABLE_COLUMN;

    /** The display string to show in the table header. */
    header: string;

    /** screen reader accessibility label */
    ariaLabel?: string,
  }

  /** See {@link CheckboxColumn}. */
  export const CHECKBOX_COLUMN = 'checkbox-column';

  /** Represents a checkbox column header (select all box) for {@link ZbpDataTableComponent}. */
  export interface CheckboxColumn extends CommonColumnProperties {
    type: typeof CHECKBOX_COLUMN;

    /** Used to update all the relevant checkboxes in the table. */
    formArray: FormArray<FormGroup<Record<string, FormControl<boolean>>>>;

    /** The display string to show in the table header. */
    header?: string;

    /** screen reader accessibility label */
    ariaLabel?: string,
  }

  /** See {@link RadioColumn}. */
  export const RADIO_COLUMN = 'radio-column';

  /** Represents a radio column header for {@link ZbpDataTableComponent}. */
  export interface RadioColumn extends CommonColumnProperties {
    type: typeof RADIO_COLUMN;

    /** The display string to show in the table header. */
    header?: string;

    /** screen reader accessibility label */
    ariaLabel?: string,

    /** Control for the radio group */
    control: FormControl,
  }

  /**
   * Represents a header in a column for {@link ZbpDataTableComponent}.
   *
   * __Developer Notes:__
   * - To add more column types, continue to expand this list, and implement the visuals in the component template.
   * - The `type` field is used to make the type safety work.
   *    - Example: `column.type === 'checkbox-column'` will make typescript collapse the type to `CheckboxColumn`,
   *        giving better intellisense in the IDE and better compile-time checking.
   */
  export type Column = (
    SimpleColumn
    | BlankColumn
    | SortableColumn
    | CheckboxColumn
    | RadioColumn
    );

  export interface PageChangeEvent {
    page: number,
    pagination: NgbPagination,
  }
}
